import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import Portal from '@material-ui/core/Portal';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import {
  DotNetValidationProblemDetailsTyped,
  CourseEntity,
  ClassEntity,
  LanguageEntity,
  MajorEntity,
  StudyLevelEntity,
  StudyYearEntity,
  UniversityEntity,
  UniversityOpeningForOutogingEntity,
  VseApplicationEntity,
  VseApplicationClassEntity,
  TeachingModeEntity,
  TimeZoneEntity,
  SeasonMessageEntity,
} from '../types';
import * as VseValidation from '../utils/validate';
import { VseApplFormPersonalParticulars, validatePersonalParticulars } from './VseApplFormPersonalParticulars';
import { VseApplFormCourseSelection } from './VseApplFormCourseSelection';
import { VseApplFormClassSelection } from './VseApplFormClassSelection';
import {
  VseApplFormDocumentSelection,
} from './VseApplFormDocumentSelection';
import {
  VseApplFormDocumentEventHandler
} from './VseApplFormDocumentUpload'
import { VseApplFormDocumentUploadHandle } from '../types/VseApplFormDocumentUploadHandle';
import { VseApplicationDocumentEntity } from '../types/VseApplicationDocumentEntity';

const useStyles = makeStyles((theme: Theme) => ({
  backButton: {
    marginRight: theme.spacing(1),
  },
  buttonsBottom: {
    marginTop: theme.spacing(1),
  },
  form: {
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(1),
    margin: 'auto',
    overflow: 'hidden',
  },
  progressBar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  root: {
    height: '100%',
  },
  stepCaption: {
    fontSize: '0.7rem',
  },
  stepInstructions: {
    margin: theme.spacing(1),
  },
  hidden: {
    display: 'none',
  }
}));

export interface VseNewApplFormProps {
  subHeaderRef: React.RefObject<HTMLDivElement>,
  season: UniversityOpeningForOutogingEntity,
  seasonMessages: SeasonMessageEntity[],
  application: VseApplicationEntity,
  filteredCourses: CourseEntity[],
  classesOfCourseSelected?: ClassEntity[],
  languageOptions: LanguageEntity[],
  majorOptions: MajorEntity[],
  studyLevelOptions: StudyLevelEntity[],
  studyYearOptions: StudyYearEntity[],
  teachingModeOptions: TeachingModeEntity[],
  timeZoneOptions: TimeZoneEntity[],
  universityOptions: UniversityEntity[],
  onSubmit: (appl: VseApplicationEntity) => void,
  onCourseFiltering: () => void,
  onCourseSelected: (selected: CourseEntity) => void,
  documentHandles: Array<VseApplFormDocumentUploadHandle>,
  onDocumentUpload: VseApplFormDocumentEventHandler,
  onDocumentCancel: VseApplFormDocumentEventHandler,
  onValidationProblem: (valProblems: DotNetValidationProblemDetailsTyped<VseApplicationEntity>) => void,
  onNextStep: () => void,
};

export function VseNewApplForm(props: VseNewApplFormProps) {
  function getLanguageEntity(code: string): LanguageEntity | undefined {
    return props.languageOptions.find((e) => e.code === code);
  }

  function getTeachingMode(code: string): TeachingModeEntity | undefined {
    return props.teachingModeOptions.find((e) => e.code === code);
  }

  function getTimeZone(code: string): TimeZoneEntity | undefined {
    return props.timeZoneOptions.find((e) => e.code === code);
  }

  function getUniversityEntity(id: number): UniversityEntity | undefined {
    return props.universityOptions.find((e) => e.universityID === id);
  }

  function getSeasonMessageEntity(code: string) {
    const msg = props.seasonMessages.find(e => {
      return e.sectionCode.trimEnd() === 'APPL'
        && e.messageCode.trimEnd() === code;
    });
    return msg ? (
      <div dangerouslySetInnerHTML={{ __html: msg.content }} />
    ) : undefined;
  }

  function handleNextStepClick() {
    let valProblems: DotNetValidationProblemDetailsTyped<VseApplicationEntity> = VseValidation.createValidationProblemDetails();
    switch (activeStep) {
      case 0:
        validatePersonalParticulars(valProblems, applState);
        break;
      case 1:
        if (!courseSelected) {
          VseValidation.appendErrorTo(valProblems, "courseSelected", "Please select 1 course");
        }
        break;
      case 2:
        if (applState.classes.length === 0) {
          VseValidation.appendErrorTo(valProblems, "classes", "Please select 1 class");
        }
        break;
      case 3:
        const documents = props.documentHandles.filter(d => d.uploading || (d.config.required && !d.uploaded));
        documents.forEach(d => {
          if (d.uploading) {
            VseValidation.appendErrorTo(valProblems, "documents", `${d.config.title} is being uploaded, please wait`);
          } else if (d.config.required && !d.uploaded) {
            VseValidation.appendErrorTo(valProblems, "documents", `Please upload ${d.config.title}`);
          }
        })
        break;
    }

    if (VseValidation.isValid(valProblems)) {
      props.onNextStep();
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      props.onValidationProblem(valProblems);
    }
  }

  function getInstruction(step: number) {
    const code = 'STEP.' + (step + 1).toString();
    return (step !== 4) ? getSeasonMessageEntity(code) : undefined;
  }
  const theme = useTheme();
  const classes = useStyles(theme);
  const [applState, setApplState] = React.useState<VseApplicationEntity>(props.application);
  const [courseSelected, setCourseSelected] = React.useState<CourseEntity>();

  const steps = [
    'Personal Particulars',
    'Course Selection',
    'Class Selection',
    'Document(s) Upload',
    'Confirmation'
  ];

  const [activeStep, setActiveStep] = React.useState(0);

  const btnNextOrSubmit = (activeStep === steps.length - 1 ? (
    <Button variant="contained" color="primary" onClick={() => {
      const documents: Array<VseApplicationDocumentEntity> = props.documentHandles.filter((d) => d.uploaded).map((d) => {
        return {
          applicationID: applState.applicationID,
          fileID: d.fileID!,
          documentType: d.config.documentType,
          fileName: d.fileName!,
          contentType: d.contentType!,
        };
      })
      props.onSubmit({ ...applState, documents })
    }}>Submit</Button>
  ) : (
    <Button
      variant="contained"
      color="primary"
      onClick={() => {
        handleNextStepClick();
      }}
    >Next</Button>
  )
  );

  // TODO: Move progress bar to new element
  return (
    <div className={classes.root}>
      <Portal container={props.subHeaderRef.current}>
        <AppBar
          color="primary"
          position="static"
          elevation={0}
        >
          <Toolbar>
            <Typography color="inherit" variant="h5" component="h1">New Application for {props.season.description}</Typography>
          </Toolbar>
        </AppBar>
      </Portal>
      <div className={classes.stepInstructions}>
        <Typography variant='subtitle2'>Instruction(s)</Typography>
        {getInstruction(activeStep)}
      </div>
      <Paper className={classes.paper}>
        <AppBar className={classes.progressBar} position="sticky" color="default" elevation={0}>
          <Toolbar>
            <Box display="flex" alignItems="center">
              <Box display='flex' marginRight={1}>
                {activeStep === steps.length ? <CheckCircleRoundedIcon fontSize='large' color='inherit' /> : (
                  <Box position="relative" display="inline-flex" >
                    <CircularProgress variant="determinate" color="inherit" value={activeStep < steps.length ? (((activeStep + 1) / (steps.length)) * 100) : 100} />
                    <Box
                      top={0}
                      left={0}
                      bottom={0}
                      right={0}
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" component="div" color="inherit" className={classes.stepCaption}>
                        {`${activeStep + 1} of ${steps.length}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box>
                <Typography component="div">
                  {`${steps[activeStep] ? steps[activeStep] : 'Success'}`}
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <form className={classes.form}>
          <VseApplFormPersonalParticulars
            className={clsx(activeStep !== 0 && classes.hidden)}
            data={applState}
            majorOptions={props.majorOptions}
            studyLevelOptions={props.studyLevelOptions}
            studyYearOptions={props.studyYearOptions}
            universityOptions={props.universityOptions}
            onChange={(changes) => setApplState((prev) => {
              return { ...prev, ...changes };
            })}
          />
          <VseApplFormCourseSelection
            className={clsx(activeStep !== 1 && classes.hidden)}
            data={props.filteredCourses}
            selected={courseSelected}
            onCourseFiltering={props.onCourseFiltering}
            onCourseSelected={(c) => {
              setCourseSelected(c);
              props.onCourseSelected(c);
            }}
            getUniversity={getUniversityEntity}
            getLanguageEntity={getLanguageEntity}
          />
          <VseApplFormClassSelection
            className={clsx(activeStep !== 2 && classes.hidden)}
            data={props.classesOfCourseSelected}
            getTeachingMode={getTeachingMode}
            getTimeZone={getTimeZone}
            onSelected={(c) => setApplState((prev) => {
              const vseApplClass: VseApplicationClassEntity = {
                applicationID: applState.applicationID,
                classID: c.classID,
              };
              return { ...prev, classes: [vseApplClass] };
            })}
          />
          <VseApplFormDocumentSelection
            className={clsx(activeStep !== 3 && classes.hidden)}
            handles={props.documentHandles}
            onDocumentCancel={props.onDocumentCancel}
            onDocumentUpload={props.onDocumentUpload}
            onValidationProblem={props.onValidationProblem}
          />
          <div className={clsx(activeStep !== 4 && classes.hidden)}>
            {getSeasonMessageEntity('STEP.5')}
          </div>
          <div className={clsx(activeStep !== steps.length && classes.hidden)}>
            <Typography>All steps completed</Typography>
          </div>
        </form>
      </Paper>
      <div className={classes.buttonsBottom} >
        <Button
          className={classes.backButton}
          disabled={activeStep === 0}
          onClick={() => {
            setActiveStep((prev) => prev - 1);
          }}
        >Back</Button>
        {activeStep < steps.length ? btnNextOrSubmit : undefined}
      </div>
    </div>
  );
}
